import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, AlertCircle, AlertTriangle } from "lucide-react";
import { useSupplierDuplicateCheck } from "@/hooks/useSupplierDuplicateCheck";

export interface IntroData {
  name: string;
  whatsapp: string;
}

interface QuizIntroProps {
  onStart: (data: IntroData) => void;
}

const QuizIntro = ({ onStart }: QuizIntroProps) => {
  const [formData, setFormData] = useState<IntroData>({
    name: "",
    whatsapp: "",
  });
  const [whatsappTouched, setWhatsappTouched] = useState(false);
  const { duplicates, checkField } = useSupplierDuplicateCheck();

  const formatWhatsApp = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const handleChange = (field: keyof IntroData, value: string) => {
    if (field === "whatsapp") {
      const formatted = formatWhatsApp(value);
      setFormData((prev) => ({ ...prev, whatsapp: formatted }));
      const digits = value.replace(/\D/g, "");
      if (digits.length > 2) setWhatsappTouched(true);
      if (digits.length === 11) checkField("phone", digits);
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onStart(formData);
  };

  const digits = formData.whatsapp.replace(/\D/g, "");
  const isFormValid =
    formData.name.trim().length >= 2 &&
    digits.length >= 10 &&
    !duplicates.phone;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="text-center space-y-3">
        <h2 className="text-2xl md:text-3xl font-bold text-brand-dark">
          Vamos descobrir se você precisa da plataforma?
        </h2>
        <p className="text-muted-foreground">
          Preencha seus dados para começar o diagnóstico do seu negócio.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5 max-w-md mx-auto">
        <div className="space-y-2">
          <Label htmlFor="intro-name" className="text-brand-dark">
            Nome
          </Label>
          <Input
            id="intro-name"
            type="text"
            placeholder="Seu nome"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            className="h-12 rounded-xl"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="intro-whatsapp" className="text-brand-dark">
            WhatsApp
          </Label>
          <Input
            id="intro-whatsapp"
            type="tel"
            placeholder="(00) 00000-0000"
            value={formData.whatsapp}
            onChange={(e) => handleChange("whatsapp", e.target.value)}
            onBlur={() => setWhatsappTouched(true)}
            className="h-12 rounded-xl"
            maxLength={15}
          />
          {duplicates.phone && (
            <p className="flex items-center gap-1.5 text-xs text-amber-600 mt-1">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              Esse telefone já está no nosso cadastro. Caso seja você, faça login na sua conta.
            </p>
          )}
          {whatsappTouched && digits.length > 0 && digits.length < 10 && (
            <p className="flex items-center gap-1.5 text-xs text-destructive mt-1">
              <AlertCircle className="h-3.5 w-3.5" />
              Telefone incompleto – digite DDD + número (ex: 11 99999-9999)
            </p>
          )}
        </div>

        <Button
          type="submit"
          disabled={!isFormValid}
          className="w-full h-12 bg-brand-orange hover:bg-brand-orange/90 text-white rounded-xl font-semibold text-base gap-2"
        >
          COMEÇAR
          <ArrowRight className="w-4 h-4" />
        </Button>
      </form>
    </motion.div>
  );
};

export default QuizIntro;
